// src/enduser/eventlist.jsx
// イベント一覧（公開）。ログイン不要の公開APIを呼ぶ。
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import "./eventlist.css";

const API_URL = import.meta.env.VITE_API_URL;

/* ========================================
 * ヘルパー
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, "0");

const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// "YYYY-MM-DD" をローカル日付として扱う（new Date(iso) だとUTC扱いで日付がずれる恐れがある）
const parseDate = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

// 検索用に、全角/半角・大文字/小文字・前後の空白をそろえる
const normalize = (s) => String(s || "").normalize("NFKC").toLowerCase().trim();

// 今日の日付を "YYYY-MM-DD" で返す（ローカル日付）
const todayStr = () => {
  const t = new Date();
  return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
};

// 開催日で並べ（asc: 古い順 / desc: 新しい順）、月ごとにまとめる
const groupByMonth = (events, order = "asc") => {
  const sorted = [...events].sort((a, b) => {
    const cmp = String(a.date || "").localeCompare(String(b.date || ""));
    return order === "asc" ? cmp : -cmp;
  });
  const groups = [];
  sorted.forEach((ev) => {
    const d = parseDate(ev.date);
    const key = d ? `${d.getFullYear()}年${d.getMonth() + 1}月` : "日付未定";
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(ev);
    else groups.push({ key, items: [ev] });
  });
  return groups;
};

// 見出しと、月ごとにまとめたイベント行
function EventSection({ title, groups }) {
  if (groups.length === 0) return null;
  return (
    <>
      <h2 className="pubEventsHeading">{title}</h2>
      {groups.map((group) => (
        <div key={group.key}>
          <h3 className="pubEventsMonth">{group.key}</h3>
          <div className="pubEventsList">
            {group.items.map((ev) => {
              const d = parseDate(ev.date);
              return (
                <Link
                  key={ev.event_id}
                  to={`/events/${ev.event_id}`}
                  className="pubEvent"
                >
                  <div className="pubEventDate">
                    <span className="pubEventMd">
                      {d ? `${pad2(d.getMonth() + 1)}.${pad2(d.getDate())}` : "--"}
                    </span>
                    <span className="pubEventDow">{d ? DOW[d.getDay()] : ""}</span>
                  </div>
                  <div className="pubEventBody">
                    <span className="pubEventTitle">{ev.title}</span>
                    {ev.venue && (
                      <span className="pubEventSub">[開催場所]　{ev.venue}</span>
                    )}
                    {ev.organizer && (
                      <span className="pubEventSub">[主催者]　{ev.organizer}</span>
                    )}
                  </div>
                  <svg className="pubEventChev" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B88A50" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

/* ========================================
 * コンポーネント
 * ======================================== */
export default function EnduserEventList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["events-list", "public"],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/public/events/list/`);
      if (!res.data?.success)
        throw new Error("イベント一覧の取得に失敗しました");
      return res.data.data || [];
    },
    retry: 1,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const [keyword, setKeyword] = useState("");

  const allEvents = data || [];

  // イベント名・主催者名にキーワードが含まれるものに絞り込む
  const kw = normalize(keyword);
  const events = kw
    ? allEvents.filter(
        (ev) =>
          normalize(ev.title).includes(kw) || normalize(ev.organizer).includes(kw)
      )
    : allEvents;

  // 開催日が今日以降 = 開催予定 / 過ぎたもの = 終了したイベント
  const today = todayStr();
  const upcoming = events.filter((ev) => (ev.date || "") >= today);
  const finished = events.filter((ev) => (ev.date || "") < today);

  return (
    <section className="pubEvents">
      <label className="pubEventsSearch">
        イベント名・主催者で探す
        <input
          type="search"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="例：プードル部会展"
        />
      </label>

      {isError && (
        <div className="pubEventsError">
          エラー: {error?.message || "イベント一覧の取得に失敗しました"}
        </div>
      )}

      {isLoading ? (
        <div className="pubEventsLoading">読み込み中...</div>
      ) : allEvents.length === 0 ? (
        <div className="pubEventsEmpty">公開中のイベントがありません</div>
      ) : events.length === 0 ? (
        <div className="pubEventsEmpty">該当するイベントがありません</div>
      ) : (
        <>
          <EventSection title="開催予定" groups={groupByMonth(upcoming, "asc")} />
          <EventSection title="終了したイベント" groups={groupByMonth(finished, "desc")} />
        </>
      )}
    </section>
  );
}
